import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiClock, FiShield, FiTrendingUp, FiUsers, FiDollarSign, FiSend, FiStar } = FiIcons;

const PurviewAuditPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    employees: '',
    industry: '',
    challenges: '',
    timeline: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Audit form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const auditBenefits = [
    { icon: FiShield, title: 'Data Security Assessment', description: 'Comprehensive evaluation of your current data security posture and vulnerabilities' },
    { icon: FiTrendingUp, title: 'Compliance Gap Analysis', description: 'Identify gaps in your current compliance framework and regulatory requirements' },
    { icon: FiDollarSign, title: 'ROI Projection', description: 'Calculate potential cost savings and efficiency gains from Microsoft Purview implementation' },
    { icon: FiUsers, title: 'Custom Roadmap', description: 'Personalized implementation strategy tailored to your organization\'s unique needs' }
  ];

  const auditProcess = [
    { step: '1', title: 'Initial Consultation', description: 'We discuss your current challenges and goals in a 30-minute discovery call.', duration: '30 minutes' },
    { step: '2', title: 'Data Assessment', description: 'Our experts analyze your data landscape and governance maturity.', duration: '2-3 days' },
    { step: '3', title: 'Detailed Report', description: 'Receive a comprehensive report with findings and recommendations.', duration: '24 hours' },
    { step: '4', title: 'Strategy Session', description: 'Review findings and discuss implementation strategy with our team.', duration: '60 minutes' }
  ];

  const testimonials = [
    { name: 'Jennifer Walsh', role: 'CIO, HealthTech Solutions', content: 'The Purview audit revealed critical gaps we didn\'t even know existed. Worth every minute.', rating: 5 },
    { name: 'Marcus Johnson', role: 'Data Director, Financial Corp', content: 'YourPurviewPartner\'s audit saved us from a potential $2M compliance fine. Incredible value.', rating: 5 }
  ];

  const industries = [
    'Healthcare', 'Financial Services', 'Manufacturing', 'Retail', 'Technology', 'Government', 'Education', 'Other'
  ];

  const employeeCounts = [
    '1-50', '51-200', '201-1000', '1001-5000', '5000+'
  ];

  const timelines = [
    'ASAP (Within 30 days)', '1-3 months', '3-6 months', '6+ months', 'Just exploring options'
  ];

  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-500 to-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Get Your Purview $5,000<br />Data Governance Audit
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-orange-100 max-w-4xl mx-auto">
                Discover hidden compliance risks, security vulnerabilities, and cost-saving opportunities in your current data governance strategy - with our Purview assessment, no strings attached.
              </p>
              {/* Urgency Banner */}
              <div className="bg-red-700 text-white px-6 py-4 rounded-lg inline-block mb-8">
                <SafeIcon icon={FiClock} className="inline mr-2" />
                <strong>Limited Time:</strong> Only 10 Purview audits available this month - 3 spots remaining!
              </div>
              {/* Value Props */}
              <div className="grid md:grid-cols-3 gap-6 mt-12 text-left">
                <div className="bg-white bg-opacity-10 p-6 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="text-green-400 mb-3 h-8 w-8" />
                  <h3 className="font-bold text-lg mb-2">Purview Assessment</h3>
                  <p className="text-orange-100">No hidden fees, no obligations. Just pure value.</p>
                </div>
                <div className="bg-white bg-opacity-10 p-6 rounded-lg">
                  <SafeIcon icon={FiClock} className="text-blue-400 mb-3 h-8 w-8" />
                  <h3 className="font-bold text-lg mb-2">Results in 24 Hours</h3>
                  <p className="text-orange-100">Fast turnaround with actionable insights.</p>
                </div>
                <div className="bg-white bg-opacity-10 p-6 rounded-lg">
                  <SafeIcon icon={FiShield} className="text-purple-400 mb-3 h-8 w-8" />
                  <h3 className="font-bold text-lg mb-2">Confidential & Secure</h3>
                  <p className="text-orange-100">Your data stays private and protected.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="audit-form" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-xl"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Claim Your Purview Audit Now
              </h2>
              <p className="text-gray-600 mb-8">
                Takes just 2 minutes to complete. Our experts will contact you within 4 hours.
              </p>
              {isSubmitted && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center"
                >
                  <SafeIcon icon={FiCheckCircle} className="mr-2" />
                  <div>
                    <strong>Success!</strong> Your audit request has been submitted. We'll contact you within 4 hours to schedule your assessment.
                  </div>
                </motion.div>
              )}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Work Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Your Company"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="employees" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Size *
                    </label>
                    <select
                      id="employees"
                      name="employees"
                      required
                      value={formData.employees}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="">Select employee count</option>
                      {employeeCounts.map((count, index) => (
                        <option key={index} value={count}>{count} employees</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                      Industry *
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      required
                      value={formData.industry}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="">Select your industry</option>
                      {industries.map((industry, index) => (
                        <option key={index} value={industry}>{industry}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                    Implementation Timeline *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    required
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">When do you need this implemented?</option>
                    {timelines.map((timeline, index) => (
                      <option key={index} value={timeline}>{timeline}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="challenges" className="block text-sm font-medium text-gray-700 mb-2">
                    Current Data Governance Challenges
                  </label>
                  <textarea
                    id="challenges"
                    name="challenges"
                    rows={4}
                    value={formData.challenges}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="What data governance challenges is your organization facing? (Optional but helps us provide better insights)"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-xl font-bold transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
                >
                  <SafeIcon icon={FiSend} className="mr-2" />
                  Get My Purview $5,000 Audit Now
                </button>
                <p className="text-sm text-gray-500 text-center">
                  ✓ Purview Audit ✓ No Spam ✓ Results in 24 Hours ✓ No Credit Card Required
                </p>
              </form>
            </motion.div>
            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  What You'll Get (Worth $5,000)
                </h2>
                <div className="space-y-6">
                  {auditBenefits.map((benefit, index) => (
                    <div key={index} className="bg-white p-6 rounded-lg shadow-lg flex items-start space-x-4">
                      <div className="bg-orange-100 p-3 rounded-lg flex-shrink-0">
                        <SafeIcon icon={benefit.icon} className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Testimonials */}
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">What Our Clients Say</h3>
                <div className="space-y-4">
                  {testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <SafeIcon key={i} icon={FiStar} className="text-yellow-400 h-4 w-4" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-3 italic">"{testimonial.content}"</p>
                      <div className="text-sm">
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-gray-600">{testimonial.role}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Security Badge */}
              <div className="bg-gray-100 p-6 rounded-lg text-center">
                <SafeIcon icon={FiShield} className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">Your Data is Safe</h3>
                <p className="text-gray-600 text-sm">
                  We follow strict security protocols and never share your information. All assessments are conducted under NDA.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Simple 4-Step Audit Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From initial consultation to actionable recommendations - here's exactly what happens next
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {auditProcess.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center relative"
              >
                {/* Connection Line */}
                {index < auditProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-blue-200 transform -translate-y-1/2 z-0"></div>
                )}
                <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 relative z-10">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 mb-4">{step.description}</p>
                <div className="text-sm text-blue-600 font-semibold">{step.duration}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Don't Wait - Your Competitors Are Already Moving
          </h2>
          <p className="text-xl mb-8 text-red-100">
            Every day you delay is another day of exposure to compliance risks and data breaches. Get ahead with a professional assessment.
          </p>
          <div className="bg-white text-red-600 p-6 rounded-lg mb-8 inline-block">
            <div className="font-bold text-lg">⚠️ URGENT: Only 3 Audit Spots Remaining</div>
            <div className="text-sm">Next available opening: 2 weeks out</div>
          </div>
          <a
            href="#audit-form"
            className="inline-flex items-center bg-white text-red-600 px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-2xl"
          >
            Secure My Purview Audit Now
            <SafeIcon icon={FiCheckCircle} className="ml-2" />
          </a>
          <p className="text-sm mt-6 text-red-200">
            ✓ No risk ✓ No obligations ✓ Expert analysis ✓ Actionable insights
          </p>
        </div>
      </section>
    </div>
  );
};

export default PurviewAuditPage;